import React, { useEffect, useState, useCallback,useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchStudentAttempts, fetchStudentExamStatuses } from '../../actions/auth'; // Adjust path as needed

import {
    Container, Paper, TableContainer, Table, Box, Link as MuiLink, Breadcrumbs, Tabs, Tab,
    TableHead, TableRow, TableCell, TableBody, Button, Typography, Chip, styled, useTheme, IconButton, Tooltip,
    CircularProgress, Alert, TablePagination, Collapse, alpha // Keep TablePagination & Collapse, Add alpha for styling
} from '@mui/material';
// Required Icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreTimeOutlinedIcon from '@mui/icons-material/MoreTimeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'; // Icon for fulfillment
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Icon for Past Due

import OldHeader from '../../components/oldHeader';
import LoadingOverlay from '../../components/LoadingOverlay';

// --- Helper Functions ---

const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });
    } catch (e) { console.error("Error formatting date:", dateString, e); return "Invalid Date"; }
};

// Helper for Status Chip in Attempts Tab
const getStatusChip = (attempt, currentTime) => { // Now accepts full attempt and current time
    console.log('attemp',attempt)
    const status = attempt?.status;
    let label = "Unknown";
    let color = "default";
    let variant = "filled"; // Default variant
    let icon = null; // Optional icon

    if (isPastDue(attempt, currentTime)) {
         label = "Past Due";
         color = "warning"; // Use warning color for past due
         variant = "outlined"; // Outlined to differentiate
         icon = <ErrorOutlineIcon fontSize="small" />;
    } else if (status === 'B') {
        label = "Scheduled";
        color = "info"; // Changed to info for better contrast with warning
        variant = "filled";
    } else if (status === 'P') {
        label = "In Progress";
        color = "secondary";
        variant = "filled";
    } else if (status === 'C') {
        label = "Completed";
        color = "success";
        variant = "filled";
        icon = <CheckCircleOutlineIcon fontSize="small"/> // Keep check for completed
    } else {
        variant = "outlined"; // Default unknown/error to outlined
    }
    return <Chip icon={icon} label={label} color={color} variant={variant} size='small' sx={{ fontSize: '0.75rem' }} />;
};
const isPastDue = (attempt, currentTime) => {
    if (attempt?.status !== 'B') {
        return false; // Only scheduled exams can be past due
    }
    const endTimeString = attempt?.exam_schedule?.end_time; // Use end_time for the check
    if (!endTimeString) {
        return false; // Cannot determine if no end time
    }
    try {
        const endTime = new Date(endTimeString);
        // Check if endTime is a valid date and if it's before the current time
        return endTime instanceof Date && !isNaN(endTime) && endTime < currentTime;
    } catch (e) {
        console.error("Error parsing end_time for Past Due check:", endTimeString, e);
        return false; // Treat parse errors as not past due
    }
};

// --- UPDATED: Enhanced CompletionStatusCell for the Status Tab ---
const CompletionStatusCell = React.memo(({ details, typeLabel, fulfilledByFullDetails }) => {
    const theme = useTheme();

    const renderViewResultButton = (studentExamId, buttonLabel = "Result", ariaLabelSuffix = "") => {
        const url = `/exam-details/${studentExamId}`; // ADJUST URL IF NEEDED
        return (
            <Button
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined" color="primary" size="small"
                startIcon={<VisibilityIcon fontSize="inherit"/>}
                sx={{ mt: 'auto', fontSize: '0.7rem', p: '2px 6px', textTransform: 'none', alignSelf: 'flex-start' }}
                aria-label={`View ${buttonLabel.toLowerCase()} for ${typeLabel} completion${ariaLabelSuffix}`}
            >
                {buttonLabel}
            </Button>
        );
    };

    // --- Case 1: Fulfilled by a Full Exam Completion ---
    if (fulfilledByFullDetails) {
        // (This logic remains unchanged - shows fulfillment status, not scores)
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, height: '100%', minHeight: '70px' /* Adjusted minHeight */ }}>
                 <Tooltip title={`Requirement met by Full Exam completion on ${formatDateTime(fulfilledByFullDetails.completed_exam_time)}`}>
                    <Chip
                        icon={<PlaylistAddCheckIcon fontSize="small" />}
                        label="Completed (via Full)"
                        color="success"
                        size="small"
                        variant="filled"
                        sx={{ backgroundColor: alpha(theme.palette.success.light, 0.7) }}
                    />
                </Tooltip>
                 <Typography variant="caption" display="block" sx={{ color: theme.palette.text.secondary, lineHeight: 1.2 }}>
                    Fulfilled: {formatDateTime(fulfilledByFullDetails.completed_exam_time)}
                </Typography>
                {/* Link to the *Full* exam result */}
                {renderViewResultButton(fulfilledByFullDetails.id, "Full Result", " (fulfilled by full exam)")}
            </Box>
        );
    }

    // --- Case 2: Specific Completion is Null (Not Completed) ---
    if (!details) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: '70px', height: '100%' }}>
                <Chip
                    icon={<RadioButtonUncheckedIcon fontSize="small" />}
                    label="Not Completed"
                    size="small"
                    variant="outlined"
                    sx={{ color: theme.palette.text.secondary }}
                />
            </Box>
        );
    }

    // --- Case 3: Has specific completion details (Eng/Math only, or Full itself) ---
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, height: '100%', minHeight: '70px' /* Adjusted minHeight for potential extra lines */ }}>
            {/* Completed Chip and Timestamp */}
            <Chip icon={<CheckCircleOutlineIcon fontSize="small" />} label="Completed" color="success" size="small" />
            <Tooltip title={`Completed: ${formatDateTime(details.completed_exam_time)}`}>
                <Typography variant="caption" display="block" sx={{ color: theme.palette.text.secondary, cursor: 'default', lineHeight: 1.2 }}>
                    {formatDateTime(details.completed_exam_time)}
                </Typography>
            </Tooltip>

            {/* --- Score Display Logic --- */}
            {/* Display for FULL completion */}
            {typeLabel === 'Full' && (
                <>
                    <Tooltip title={`Total Score: ${details.total_score ?? 'N/A'} | Total Correct: ${(details.eng_correct ?? 0) + (details.math_correct ?? 0)} / ${(details.eng_total ?? 0) + (details.math_total ?? 0)}`}>
                         <Typography variant="caption" display="block" sx={{ fontWeight: 'medium', cursor: 'default', lineHeight: 1.2 }}>
                            Total Score: {details.total_score ?? 'N/A'}
                        </Typography>
                    </Tooltip>
                    {/* Show English Score from the Full attempt */}
                    <Tooltip title={`English Score: ${details.eng_score ?? 'N/A'} | English Correct: ${details.eng_correct ?? 'N/A'} / ${details.eng_total ?? 'N/A'}`}>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'regular', cursor: 'default', lineHeight: 1.2, pl: 1 /* Indent slightly */ }}>
                            ↳ Eng: {details.eng_score ?? 'N/A'}
                        </Typography>
                    </Tooltip>
                     {/* Show Math Score from the Full attempt */}
                     <Tooltip title={`Math Score: ${details.math_score ?? 'N/A'} | Math Correct: ${details.math_correct ?? 'N/A'} / ${details.math_total ?? 'N/A'}`}>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'regular', cursor: 'default', lineHeight: 1.2, pl: 1 /* Indent slightly */ }}>
                            ↳ Math: {details.math_score ?? 'N/A'}
                        </Typography>
                    </Tooltip>
                </>
            )}
            {/* Display for ENGLISH completion (only if not fulfilled by full) */}
            {typeLabel === 'English' && (details.eng_total ?? 0) > 0 && (
                 <Tooltip title={`Eng Score: ${details.eng_score ?? 'N/A'} | Correct: ${details.eng_correct ?? 'N/A'} / ${details.eng_total ?? 'N/A'}`}>
                     <Typography variant="caption" display="block" sx={{ fontWeight: 'medium', cursor: 'default', lineHeight: 1.2 }}>
                        Eng Score: {details.eng_score ?? 'N/A'}
                    </Typography>
                 </Tooltip>
            )}
             {/* Display for MATH completion (only if not fulfilled by full) */}
             {typeLabel === 'Math' && (details.math_total ?? 0) > 0 && (
                 <Tooltip title={`Math Score: ${details.math_score ?? 'N/A'} | Correct: ${details.math_correct ?? 'N/A'} / ${details.math_total ?? 'N/A'}`}>
                     <Typography variant="caption" display="block" sx={{ fontWeight: 'medium', cursor: 'default', lineHeight: 1.2 }}>
                        Math Score: {details.math_score ?? 'N/A'}
                    </Typography>
                 </Tooltip>
            )}

            {/* Result Button (links to the specific details.id) */}
            {renderViewResultButton(details.id, "Result")}
        </Box>
    );
});
CompletionStatusCell.displayName = 'CompletionStatusCell'; // Add display name for DevTools


// Kept ModuleScoreBar for the Attempts Tab
function ModuleScoreBar({ score, correct, total }) { /* ... same as original ... */
    const theme = useTheme();
    const isValidScore = typeof score === 'number' && !isNaN(score);
    const isValidCorrect = typeof correct === 'number' && !isNaN(correct) && correct >= 0;
    const isValidTotal = typeof total === 'number' && !isNaN(total) && total > 0;
    let percentage = isValidTotal && isValidCorrect ? (correct / total) * 100 : NaN;
    if (!isValidTotal || !isValidCorrect || !isValidScore || isNaN(percentage)) { return ( <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 58 }}><Tooltip title="Not Available / Not Applicable" placement="top"><InfoOutlinedIcon color="action" /></Tooltip><Typography variant="caption" sx={{ ml: 1, color: theme.palette.text.disabled }}>N/A</Typography></Box> ); }
    const getColor = () => { if (percentage >= 75) return theme.palette.success.main; if (percentage >= 50) return theme.palette.warning.main; return theme.palette.error.main; };
    return ( <Box sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', backgroundColor: theme.palette.background.paper, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}><Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', mr: 1, flexShrink: 0 }}><Typography variant="subtitle2" component="div" sx={{ fontWeight: 'medium' }}>Score: {score}</Typography><Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{`${correct}/${total} Correct`}</Typography></Box><Box sx={{ position: 'relative', display: 'inline-flex' }}><CircularProgress variant="determinate" value={percentage} size={40} thickness={4} style={{ color: getColor() }} /><Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography variant="caption" component="div" color="text.primary" sx={{ fontWeight: 'medium' }}>{`${Math.round(percentage)}%`}</Typography></Box></Box></Box> );
}

// Helper for rendering breakdown rows (from original code)
const renderBreakdownRow = (label, correct, total) => {
    const isValidTotal = typeof total === 'number' && total >= 0 && !isNaN(total);
    const isValidCorrect = typeof correct === 'number' && correct >= 0 && !isNaN(correct);
    if (!isValidTotal || !isValidCorrect || total === 0) { return ( <TableRow key={label}><TableCell>{label}</TableCell><TableCell align="right">{isValidCorrect ? correct : 'N/A'}</TableCell><TableCell align="right">N/A</TableCell><TableCell align="right">{isValidTotal ? total : 'N/A'}</TableCell><TableCell align="right">N/A</TableCell></TableRow> ); }
    const incorrect = Math.max(0, total - correct);
    const percentage = Math.round((correct / total) * 100);
    return ( <TableRow key={label}><TableCell>{label}</TableCell><TableCell align="right">{correct}</TableCell><TableCell align="right">{incorrect}</TableCell><TableCell align="right">{total}</TableCell><TableCell align="right">{`${percentage}%`}</TableCell></TableRow> );
};


// ========================================================================
// OneStudent Component with Tabs
// ========================================================================
const OneStudent = () => {
    // --- State ---
    const [studentInfo, setStudentInfo] = useState(null);
    const [activeTab, setActiveTab] = useState(0); // 0 = Attempts, 1 = Status

    // Attempts Tab State
    const [studentExams, setStudentExams] = useState([]);
    const [isLoadingAttempts, setIsLoadingAttempts] = useState(false); // Set to false initially
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalAttemptsCount, setTotalAttemptsCount] = useState(0);
    const [expandedAttempts, setExpandedAttempts] = useState({});
    const [errorAttempts, setErrorAttempts] = useState(null); // Separate error state for attempts

    // Overall Status Tab State
    const [examStatuses, setExamStatuses] = useState([]);
    const [isLoadingExamStatuses, setIsLoadingExamStatuses] = useState(false);
    const [errorExamStatuses, setErrorExamStatuses] = useState(null);
    const [hasLoadedExamStatuses, setHasLoadedExamStatuses] = useState(false);

    // Hooks
    const { studentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // --- Fetch Student Attempts (Paginated - for Tab 0) ---
    const fetchAttemptsCallback = useCallback(async (currentPage, currentRowsPerPage) => {
        if (!studentId) return;
        setIsLoadingAttempts(true);
        setErrorAttempts(null); // Clear previous attempt errors
        try {
            const response = await fetchStudentAttempts(studentId, currentPage + 1, currentRowsPerPage);
            console.log('response',response)
            setStudentExams(response.results || []);
            setTotalAttemptsCount(response.count || 0);
            // Get student info from first attempt if available and not set
            if (!studentInfo && response.results?.length > 0 && response.results[0].student_info) {
                setStudentInfo(response.results[0].student_info);
            } else if (!studentInfo) {
                 // Basic fallback if no attempts or attempts lack student info
                 setStudentInfo({ id: studentId, name: `Student ${studentId}` });
            }
        } catch (error) {
            console.error("Error fetching student attempts:", error);
            setErrorAttempts(`Failed to load attempts: ${error.message || 'Unknown error'}`);
            setStudentExams([]);
            setTotalAttemptsCount(0);
        } finally {
            setIsLoadingAttempts(false);
        }
    }, [studentId, studentInfo]); // studentInfo dependency helps update header if loaded later

    // --- Fetch Exam Statuses (Non-Paginated, On Demand - for Tab 1) ---
    const handleLoadExamStatuses = useCallback(async () => {
        if (!studentId || isLoadingExamStatuses) return;
        setIsLoadingExamStatuses(true);
        setHasLoadedExamStatuses(true); // Mark as attempted load
        setErrorExamStatuses(null);
        setExamStatuses([]); // Clear previous results

        try {
            const data = await fetchStudentExamStatuses(studentId);
             // API returns array directly based on example
            setExamStatuses(data || []);
            // Potentially update student name if status call has more accurate info (optional)
            // if (!studentInfo && data?.student_info) { // Adjust if API includes student_info at top level
            //     setStudentInfo(data.student_info);
            // }
        } catch (err) {
            console.error("Error fetching student exam statuses:", err);
            setErrorExamStatuses(`Failed to load exam statuses: ${err.message || 'Unknown error'}`);
            setExamStatuses([]);
        } finally {
            setIsLoadingExamStatuses(false);
        }
    }, [studentId, isLoadingExamStatuses]); // Removed studentInfo dependency here

    // Effect to load attempts when component mounts or relevant state changes
    useEffect(() => {
        // Load attempts only if Tab 0 is active AND they haven't been loaded yet OR pagination changed
        // This prevents re-fetching when switching back to Tab 0 unless page/rows change
        if (activeTab === 0) {
            fetchAttemptsCallback(page, rowsPerPage);
        }
    }, [activeTab, page, rowsPerPage, fetchAttemptsCallback]); // Dependencies for attempts fetch

    // --- Handlers ---
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Load Status tab data only when switched to AND if not already loaded
        if (newValue === 1 && !hasLoadedExamStatuses && !isLoadingExamStatuses) {
            handleLoadExamStatuses();
        }
    };

    const handleChangePage = (event, newPage) => { setPage(newPage); };
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
    const navigateHome = () => { if (location.state?.schedule) { navigate('/schedules'); } else { navigate('/team'); } };
    const toggleExpandAttempt = (examAttemptId) => {
        setExpandedAttempts((prev) => ({ ...prev, [examAttemptId]: !prev[examAttemptId] }));
    };


    // --- Render Logic ---
    const headerTitle = studentInfo?.name || `Student ${studentId || ''}`.trim();
    const now = useMemo(() => new Date(), []); // Get current time once per render cycle
    const sortedStudentExams = useMemo(() => {
        const examsCopy = [...studentExams];
        examsCopy.sort((a, b) => {
            const aIsPastDue = isPastDue(a, now);
            const bIsPastDue = isPastDue(b, now);

            if (aIsPastDue && !bIsPastDue) {
                return 1; // a (Past Due) goes to the bottom
            }
            if (!aIsPastDue && bIsPastDue) {
                return -1; // b (Past Due) goes to the bottom (relative to a)
            }

            // If both have the same pastDue status (both true or both false),
            // sort by scheduled start time descending (most recent first)
            const timeA = new Date(a.exam_schedule?.start_time).getTime();
            const timeB = new Date(b.exam_schedule?.start_time).getTime();

            if (!isNaN(timeA) && !isNaN(timeB)) {
                return timeB - timeA; // Descending order
            } else if (!isNaN(timeA)) {
                return -1; // Place valid time A before invalid time B
            } else if (!isNaN(timeB)) {
                return 1; // Place valid time B before invalid time A
            }
            return 0; // Keep original order if times are invalid or equal
        });
        return examsCopy;
    }, [studentExams]); // Recalculate when attempts list changes
    return (
        <Container maxWidth="lg" sx={{ pb: 4 }}>
            {/* Breadcrumbs and Header */}
            <Box sx={{ my: 2 }}>
                 <Breadcrumbs aria-label="breadcrumb">
                     <MuiLink underline="hover" color="inherit" onClick={navigateHome} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}> {location.state?.schedule ? 'Schedules' : 'Team'} </MuiLink>
                     <Typography color="text.primary">{headerTitle} - Exams</Typography>
                 </Breadcrumbs>
             </Box>
             <OldHeader title={headerTitle} subtitle="View student's exam history and overall status" />

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="student exam views" variant="fullWidth">
                    <Tab label="Exam Attempts" icon={<HistoryIcon />} iconPosition="start" id="tab-attempts" aria-controls="tabpanel-attempts" />
                    <Tab label="Overall Exam Status" icon={<FactCheckOutlinedIcon />} iconPosition="start" id="tab-status" aria-controls="tabpanel-status" />
                </Tabs>
            </Box>

            {/* --- Tab Panel for Exam Attempts (Tab 0) --- */}
            <Box role="tabpanel" hidden={activeTab !== 0} id="tabpanel-attempts" aria-labelledby="tab-attempts">
                {activeTab === 0 && (
                    <>
                        {/* Handle loading and error states for Attempts */}
                        {isLoadingAttempts && sortedStudentExams.length === 0 && <LoadingOverlay message="Loading Attempts..." />}
                        {errorAttempts && <Alert severity="error" sx={{ mt: 2 }}>{errorAttempts}</Alert>}
                        {!isLoadingAttempts && !errorAttempts && totalAttemptsCount === 0 && (
                            <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}> No past or current exam attempts found. </Typography>
                        )}

                        {/* Render table only if there are attempts or it's loading more pages */}
                        {(totalAttemptsCount > 0 || (isLoadingAttempts && sortedStudentExams.length > 0)) && !errorAttempts && (
                             <Paper elevation={2} sx={{ mt: 2, overflow: 'hidden' }}>
                                 <TableContainer>
                                     <Table size="small" aria-label="student exam attempts table">
                                         <TableHead>
                                              <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: theme.palette.action.hover, fontWeight: 'bold' } }}>
                                                  <TableCell sx={{ minWidth: 100 }}>Exam Name</TableCell>

                                                  <TableCell align="center">Status</TableCell>
                                                  <TableCell align="center" sx={{ minWidth: 150 }}>Total Score</TableCell>
                                                  <TableCell align="center" sx={{ minWidth: 150 }}>English Score</TableCell>
                                                  <TableCell align="center" sx={{ minWidth: 150 }}>Math Score</TableCell>
                                                  <TableCell align="center"sx={{ minWidth: 150 }}>Scheduled Time</TableCell>
                                                  {/* <TableCell align="center"sx={{ minWidth: 150 }}>Started Time</TableCell> */}

                                                  <TableCell align="center" sx={{ minWidth: 150 }}>Completed</TableCell>
                                                  <TableCell align="center">Actions</TableCell>
                                              </TableRow>
                                          </TableHead>
                                          <TableBody>
                                             {sortedStudentExams.map((examAttempt) => (
                                                  <React.Fragment key={`attempt-${examAttempt.id}`}>
                                                      <TableRow hover>
                                                           {/* Exam Name Cell */}
                                                           <TableCell sx={{ whiteSpace: 'normal'}}>
                                                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'medium' }}>
                                                                       {examAttempt.exam_schedule?.exam_name || 'Exam Name Missing'} - {examAttempt.exam_schedule?.schedule_type || 'Type Missing'}
                                                                    </Typography>
                                                                    {(examAttempt.accommodation_multiplier && parseFloat(examAttempt.accommodation_multiplier) !== 1.0) && ( <Tooltip title={`Time Accommodation: ${examAttempt.accommodation_multiplier}x`}><IconButton size="small" sx={{ p:0.1 }}><MoreTimeOutlinedIcon color="info" sx={{ fontSize: '1.1rem' }} /></IconButton></Tooltip> )}
                                                                </Box>
                                                           </TableCell>
                                                           {/* Status Cell */}
                                                           <TableCell align="center">{getStatusChip(examAttempt, now)}</TableCell>
                                                           {/* Score Cells - Conditionally render ModuleScoreBar */}
                                                           <TableCell align="center">
                                                                {(examAttempt.exam_schedule?.schedule_type === 'Full' || (examAttempt.exam_schedule?.schedule_type !== 'English' && examAttempt.exam_schedule?.schedule_type !== 'Math')) && examAttempt.status === 'C' ? (
                                                                    <ModuleScoreBar score={examAttempt.math_score + examAttempt.eng_score} correct={(examAttempt.eng_correct ?? 0) + (examAttempt.math_correct ?? 0)} total={(examAttempt.eng_total ?? 0) + (examAttempt.math_total ?? 0)}/>
                                                                ) : ( <Chip size="small" label="N/A" variant="outlined"/> )}
                                                           </TableCell>
                                                           <TableCell align="center">
                                                                {examAttempt.exam_schedule?.schedule_type !== 'Math' && examAttempt.status === 'C' ? (
                                                                    <ModuleScoreBar score={examAttempt.eng_score} correct={examAttempt.eng_correct} total={examAttempt.eng_total} />
                                                                ) : ( <Chip size="small" label="N/A" variant="outlined"/> )}
                                                           </TableCell>
                                                           <TableCell align="center">
                                                                {examAttempt.exam_schedule?.schedule_type !== 'English' && examAttempt.status === 'C' ? (
                                                                    <ModuleScoreBar score={examAttempt.math_score} correct={examAttempt.math_correct} total={examAttempt.math_total} />
                                                                ) : ( <Chip size="small" label="N/A" variant="outlined"/> )}
                                                           </TableCell>
                                                           {/* Completed Time Cell */}
                                                           <TableCell align="center">{formatDateTime(examAttempt.exam_schedule?.start_time)}</TableCell>
                                                           {/* <TableCell align="center">{formatDateTime(new Date(examAttempt?.exam_start_time))}</TableCell> */}

                                                           <TableCell align="center">{formatDateTime(examAttempt?.completed_exam_time)}</TableCell>
                                                           {/* Actions Cell */}
                                                           <TableCell align="center">
                                                               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0 }}>
                                                                   {/* Expand Icon */}
                                                                   <Tooltip title={expandedAttempts[examAttempt.id] ? "Hide Breakdown" : "Show Breakdown"}><IconButton onClick={() => toggleExpandAttempt(examAttempt.id)} size="small" aria-label="expand row" disabled={examAttempt.status !== 'C'}><ExpandMoreIcon sx={{ transform: expandedAttempts[examAttempt.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} /></IconButton></Tooltip>
                                                                   {/* Review Button (opens new tab) */}
                                                                    <Button component="a" href={`/exam-details/${examAttempt.id}`} target="_blank" rel="noopener noreferrer" variant="text" color="primary" size="small" disabled={examAttempt.status !== 'C'} sx={{ whiteSpace: 'nowrap', ml: 0.5 }} > Review </Button>
                                                                </Box>
                                                           </TableCell>
                                                       </TableRow>
                                                       {/* Collapsible Row for Detailed Breakdown */}
                                                       <TableRow>
                                                            <TableCell sx={{ py: 0, borderBottom: expandedAttempts[examAttempt.id] ? '1px solid rgba(224, 224, 224, 1)' : 0 }} colSpan={8}>
                                                                <Collapse in={expandedAttempts[examAttempt.id]} timeout="auto" unmountOnExit>
                                                                     <Box sx={{ m: 1, p: 2, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)', borderRadius: '4px' }}>
                                                                        <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '0.9rem', fontWeight: 'medium' }}>Detailed Breakdown</Typography>
                                                                        <Table size="small" aria-label="detailed breakdown">
                                                                            <TableHead><TableRow sx={{'& .MuiTableCell-root': {fontWeight:'bold', fontSize: '0.75rem'}}}><TableCell>Section</TableCell><TableCell align="right">Correct</TableCell><TableCell align="right">Incorrect</TableCell><TableCell align="right">Total Qs</TableCell><TableCell align="right">% Correct</TableCell></TableRow></TableHead>
                                                                            <TableBody>
                                                                                {/* Improved breakdown rendering safety */}
                                                                                {examAttempt.exam_schedule?.schedule_type !== 'Math' && (examAttempt.eng_total ?? 0) > 0 && (
                                                                                    <>
                                                                                        {renderBreakdownRow('English Part 1', examAttempt.eng1_correct, Math.floor(examAttempt.eng_total / 2))}
                                                                                        {renderBreakdownRow('English Part 2', examAttempt.eng2_correct, Math.ceil(examAttempt.eng_total / 2))}
                                                                                    </>
                                                                                )}
                                                                                {examAttempt.exam_schedule?.schedule_type !== 'English' && (examAttempt.math_total ?? 0) > 0 && (
                                                                                     <>
                                                                                         {renderBreakdownRow('Math Part 1', examAttempt.math1_correct, Math.floor(examAttempt.math_total / 2))}
                                                                                         {renderBreakdownRow('Math Part 2', examAttempt.math2_correct, Math.ceil(examAttempt.math_total / 2))}
                                                                                     </>
                                                                                )}
                                                                                {/* Add Overall Total Row only if BOTH sections exist (implicitly Full) */}
                                                                                {(examAttempt.eng_total ?? 0) > 0 && (examAttempt.math_total ?? 0) > 0 &&
                                                                                    renderBreakdownRow('Overall Total', (examAttempt.eng_correct ?? 0) + (examAttempt.math_correct ?? 0), (examAttempt.eng_total ?? 0) + (examAttempt.math_total ?? 0))
                                                                                }
                                                                            </TableBody>
                                                                        </Table>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                  </React.Fragment>
                                             ))}
                                             {/* Loading indicator during pagination */}
                                             {isLoadingAttempts && sortedStudentExams.length > 0 && (
                                                <TableRow>
                                                     <TableCell colSpan={8} align="center"><CircularProgress size={24} sx={{my: 1}}/></TableCell>
                                                 </TableRow>
                                             )}
                                         </TableBody>
                                     </Table>
                                 </TableContainer>
                                 {/* Pagination for Attempts */}
                                 <TablePagination
                                     rowsPerPageOptions={[5, 10, 25]}
                                     component="div"
                                     count={totalAttemptsCount}
                                     rowsPerPage={rowsPerPage}
                                     page={page}
                                     onPageChange={handleChangePage}
                                     onRowsPerPageChange={handleChangeRowsPerPage}
                                     labelRowsPerPage="Attempts per page:"
                                 />
                             </Paper>
                        )}
                    </>
                )}
            </Box>

             {/* --- Tab Panel for Overall Exam Status (Tab 1) --- */}
            <Box role="tabpanel" hidden={activeTab !== 1} id="tabpanel-status" aria-labelledby="tab-status">
                 {activeTab === 1 && (
                     <>
                        {/* Initial loading state */}
                        {isLoadingExamStatuses && !hasLoadedExamStatuses && <LoadingOverlay message="Loading Exam Statuses..."/>}
                        {/* Subsequent loading state (e.g., manual refresh if added) */}
                        {isLoadingExamStatuses && hasLoadedExamStatuses && <LoadingOverlay message="Refreshing Exam Statuses..."/>}
                        {/* Error display */}
                        {errorExamStatuses && <Alert severity="error" sx={{ mt: 2 }}>{errorExamStatuses}</Alert>}

                        {/* Content when loaded successfully */}
                        {!isLoadingExamStatuses && !errorExamStatuses && hasLoadedExamStatuses && (
                             <>
                                {examStatuses.length === 0 ? (
                                    <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}> No published exams found to check status against for this student. </Typography>
                                ) : (
                                    <Paper elevation={2} sx={{ mt: 2, overflow: 'hidden' }}>
                                        <TableContainer> {/* Adjusted max height */}
                                            <Table stickyHeader aria-label="overall exam status table" size="medium">
                                                 <TableHead>
                                                     <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: theme.palette.action.selected, fontWeight: 'bold', whiteSpace: 'nowrap' } }}>
                                                         <TableCell sx={{ minWidth: 170, position: 'sticky', left: 0, zIndex: 1, backgroundColor: theme.palette.action.selected }}>Exam</TableCell>
                                                         {/* <TableCell align="center" sx={{ minWidth: 80 }}>Type</TableCell> */}
                                                         <TableCell align="left" sx={{ minWidth: 180 }}>Full Completion</TableCell>
                                                         <TableCell align="left" sx={{ minWidth: 180 }}>English Completion</TableCell>
                                                         <TableCell align="left" sx={{ minWidth: 180 }}>Math Completion</TableCell>
                                                     </TableRow>
                                                 </TableHead>
                                                 <TableBody>
                                                     {examStatuses.map((exam) => (
                                                         <TableRow hover key={exam.id}>
                                                             {/* Exam Name (Sticky) */}
                                                             <TableCell component="th" scope="row" sx={{ position: 'sticky', left: 0, zIndex: 0, backgroundColor: theme.palette.background.paper /* Ensure bg for overlap */ }}>
                                                                 <Typography variant="body2" sx={{ fontWeight: 500 }}>{exam.name}</Typography>
                                                             </TableCell>
                                                             {/* Exam Type */}
                                                             {/* <TableCell align="center">
                                                                 <Chip label={exam.exam_type || 'N/A'} size="small" variant='outlined'/>
                                                             </TableCell> */}
                                                             {/* Full Completion Cell */}
                                                             <TableCell align="left" sx={{ verticalAlign: 'top', p: 1 }}>
                                                                 <CompletionStatusCell
                                                                     details={exam.full_completion_details}
                                                                     typeLabel="Full"
                                                                     // Not fulfilled by anything else
                                                                 />
                                                             </TableCell>
                                                             {/* English Completion Cell - Check if fulfilled by Full */}
                                                             <TableCell align="left" sx={{ verticalAlign: 'top', p: 1 }}>
                                                                 <CompletionStatusCell
                                                                     details={exam.english_completion_details} // Pass the specific English details
                                                                     typeLabel="English"
                                                                     // Pass full details ONLY IF full is completed
                                                                     fulfilledByFullDetails={exam.full_completion_details ? exam.full_completion_details : null}
                                                                 />
                                                             </TableCell>
                                                             {/* Math Completion Cell - Check if fulfilled by Full */}
                                                             <TableCell align="left" sx={{ verticalAlign: 'top', p: 1 }}>
                                                                 <CompletionStatusCell
                                                                     details={exam.math_completion_details} // Pass the specific Math details
                                                                     typeLabel="Math"
                                                                     // Pass full details ONLY IF full is completed
                                                                     fulfilledByFullDetails={exam.full_completion_details ? exam.full_completion_details : null}
                                                                 />
                                                             </TableCell>
                                                         </TableRow>
                                                     ))}
                                                 </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {/* No Pagination needed here */}
                                    </Paper>
                                )}
                            </>
                         )}
                         {/* Message shown before the status tab is initially loaded */}
                         {!hasLoadedExamStatuses && !isLoadingExamStatuses && !errorExamStatuses && (
                            <Box sx={{ textAlign: 'center', mt: 4, p: 2, border: `1px dashed ${theme.palette.divider}`, borderRadius: 1 }}>
                                <FactCheckOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">
                                    This tab shows the student's overall completion status for each published exam.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    Clicking the tab automatically loads this data.
                                </Typography>
                                {/* Optional manual load button if auto-load on tab click is removed */}
                            </Box>
                         )}
                     </>
                 )}
            </Box>

        </Container>
    );
};

export default OneStudent;